import { useState } from "react";
import { X } from "lucide-react";
import { PROPERTY_IMAGES, ATMOSPHERIC_IMAGES } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/lib/useSEO";

const GalleryPage = () => {
  const { t, lang } = useLang();
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useSEO({
    title: lang === "en" ? "Photo Gallery" : "Galleria fotografica",
    description: lang === "en"
      ? "Explore every detail of La dimora di Patty: bedrooms, living, bathroom, and the atmospheres of Castel di Sangro and the Abruzzo Apennines."
      : "Esplora ogni dettaglio della dimora: camere, zona giorno, bagno e le atmosfere di Castel di Sangro e dell'Appennino abruzzese.",
  });

  const ALL_IMAGES = [
    { src: PROPERTY_IMAGES.esterno, label: t("gallery.cat_exterior"), cat: "exterior" },
    { src: PROPERTY_IMAGES.camera, label: t("gallery.cat_bedrooms"), cat: "bedrooms" },
    { src: PROPERTY_IMAGES.cameraAlt, label: t("gallery.cat_bedrooms"), cat: "bedrooms" },
    { src: PROPERTY_IMAGES.cameraAlt2, label: t("gallery.cat_bedrooms"), cat: "bedrooms" },
    { src: PROPERTY_IMAGES.cucina, label: t("gallery.cat_living"), cat: "living" },
    { src: PROPERTY_IMAGES.cucinaAlt, label: t("gallery.cat_living"), cat: "living" },
    { src: PROPERTY_IMAGES.ingressoAlt, label: t("gallery.cat_living"), cat: "living" },
    { src: PROPERTY_IMAGES.cameretta, label: t("gallery.cat_bedrooms"), cat: "bedrooms" },
    { src: PROPERTY_IMAGES.camerettaAlt, label: t("gallery.cat_bedrooms"), cat: "bedrooms" },
    { src: PROPERTY_IMAGES.camerettaAlt2, label: t("gallery.cat_bedrooms"), cat: "bedrooms" },
    { src: PROPERTY_IMAGES.bagno, label: t("gallery.cat_bath"), cat: "bath" },
    { src: PROPERTY_IMAGES.bagnoAlt, label: t("gallery.cat_bath"), cat: "bath" },
    { src: PROPERTY_IMAGES.ingresso, label: t("gallery.cat_interior"), cat: "interior" },
    { src: ATMOSPHERIC_IMAGES.heroTerrace, label: t("gallery.cat_atmosphere"), cat: "atmosphere" },
    { src: ATMOSPHERIC_IMAGES.exterior, label: t("gallery.cat_atmosphere"), cat: "atmosphere" },
  ];

  const CATEGORIES = [
    { key: "all", label: t("gallery.cat_all") },
    { key: "exterior", label: t("gallery.cat_exterior") },
    { key: "bedrooms", label: t("gallery.cat_bedrooms") },
    { key: "living", label: t("gallery.cat_living") },
    { key: "bath", label: t("gallery.cat_bath") },
    { key: "interior", label: t("gallery.cat_interior") },
    { key: "atmosphere", label: t("gallery.cat_atmosphere") },
  ];

  const filtered = filter === "all" ? ALL_IMAGES : ALL_IMAGES.filter((i) => i.cat === filter);

  return (
    <main data-testid="gallery-page" className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal className="text-center mb-12">
          <div className="overline text-accent gold-line-center mb-6">{t("gallery.overline")}</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-tight">
            {t("gallery.page_title")}
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto mt-6">
            {t("gallery.page_subtitle")}
          </p>
        </Reveal>

        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-12">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={`gallery-filter-${c.key}`}
              onClick={() => setFilter(c.key)}
              className={`px-5 py-2 rounded-full text-sm border transition-colors ${
                filter === c.key
                  ? "bg-foreground text-background border-foreground"
                  : "border-border text-muted-foreground hover:border-accent hover:text-accent"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
          {filtered.map((img, i) => (
            <Reveal key={`${img.src}-${i}`} delay={i * 50}>
              <button
                data-testid={`gallery-image-${i}`}
                onClick={() => setLightbox(img)}
                className="group block w-full break-inside-avoid relative overflow-hidden rounded-2xl"
              >
                <img src={img.src} alt={img.label} loading="lazy" decoding="async" className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-4 left-4 text-white translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                  <div className="overline text-accent">{img.cat}</div>
                  <div className="font-serif text-xl">{img.label}</div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      {lightbox && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 animate-fade-up"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="gallery-lightbox-close"
            className="absolute top-6 right-6 text-white hover:text-accent"
            onClick={() => setLightbox(null)}
          >
            <X size={28} />
          </button>
          <img src={lightbox.src} alt={lightbox.label} className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg" />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-center">
            <div className="overline text-accent">{lightbox.cat}</div>
            <div className="font-serif text-2xl">{lightbox.label}</div>
          </div>
        </div>
      )}
    </main>
  );
};

export default GalleryPage;
